import {
  DiffEditor as MonacoDiffEditor,
  Editor as MonacoEditor,
} from "@monaco-editor/react";
import {
  CodeRounded as MonacoIcon,
  TextFieldsRounded as SimpleIcon,
} from "@mui/icons-material";
import { GlobalStyles, ToggleButton, ToggleButtonGroup } from "@mui/material";
import ReactCodeEditor from "@uiw/react-textarea-code-editor";
import { useCallback, useEffect, useRef } from "react";
import ReactDiffViewer from "react-diff-viewer-continued";
import { useLocalStorageStringState } from "store/hooks/stateHooks";

const useEditorPreference = () =>
  useLocalStorageStringState("preferred-code-editor", { default: "monaco" });

const defineThemes = (monaco) => {
  monaco.editor.defineTheme("cactus-light", {
    base: "vs",
    inherit: true,
    rules: [
      { token: "string.key.json", foreground: "218058" },
      { token: "string.value.json", foreground: "a45a31" },
      { token: "number", foreground: "30ac65" },
      { token: "keyword", foreground: "485159" },
      { token: "comment", foreground: "9297a2" },
    ],
    colors: {
      "editor.background": "#f5f7f9",
      "editor.foreground": "#45494e",
      "editor.lineHighlightBackground": "#edf0f3",
      "editor.selectionBackground": "#c3e8d0",
      "editorLineNumber.foreground": "#9297a2",
      "editorLineNumber.activeForeground": "#45494e",
      "editorCursor.foreground": "#30ac65",
      "editor.inactiveSelectionBackground": "#ebfdf1",
      "editorIndentGuide.background": "#dfe5ea",
      "editorIndentGuide.activeBackground": "#9ad3af",
      "editorGutter.background": "#f5f7f9",
      "minimap.background": "#f5f7f9",
      "scrollbarSlider.background": "#dfe5ea80",
      "scrollbarSlider.hoverBackground": "#bdbdbd80",
    },
  });
};

export const EditorPreferenceToggle = () => {
  const [editorType, setEditorType] = useEditorPreference();

  return (
    <ToggleButtonGroup
      value={editorType}
      onChange={(_, v) => {
        if (v) setEditorType(v);
      }}
      exclusive
      size={"small"}
    >
      <ToggleButton value={"monaco"}>
        <MonacoIcon fontSize={"small"} sx={{ mr: 0.5 }} />
        <span>{"Monaco"}</span>
      </ToggleButton>
      <ToggleButton value={"simple"}>
        <SimpleIcon fontSize={"small"} sx={{ mr: 0.5 }} />
        <span>{"Simple"}</span>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const HIGHLIGHT_STYLES = (
  <GlobalStyles
    styles={{
      ".config-drift-line": { backgroundColor: "rgba(25, 118, 210, 0.13)" },
      ".config-drift-gutter": {
        borderLeft: "3px solid #1976d2",
        marginLeft: "3px",
      },
    }}
  />
);

const toLineDecoration = (lineNumber) => ({
  range: {
    startLineNumber: lineNumber,
    startColumn: 1,
    endLineNumber: lineNumber,
    endColumn: 1,
  },
  options: {
    isWholeLine: true,
    className: "config-drift-line",
    linesDecorationsClassName: "config-drift-gutter",
  },
});

export const DiffEditor = ({
  original,
  modified,
  language = "json",
  height = "60vh",
  originalHighlightLines = [],
  modifiedHighlightLines = [],
}) => {
  const [editorType] = useEditorPreference();
  const diffEditorRef = useRef(null);
  const decorationIdsRef = useRef({ original: [], modified: [] });

  const applyHighlights = useCallback(() => {
    const diffEditor = diffEditorRef.current;
    if (!diffEditor) return;
    decorationIdsRef.current.original = diffEditor
      .getOriginalEditor()
      .deltaDecorations(
        decorationIdsRef.current.original,
        originalHighlightLines.map(toLineDecoration)
      );
    decorationIdsRef.current.modified = diffEditor
      .getModifiedEditor()
      .deltaDecorations(
        decorationIdsRef.current.modified,
        modifiedHighlightLines.map(toLineDecoration)
      );
  }, [originalHighlightLines, modifiedHighlightLines]);

  useEffect(() => {
    applyHighlights();
  }, [applyHighlights]);

  const handleEditorMount = useCallback(
    (editor, monaco) => {
      defineThemes(monaco);
      monaco.editor.setTheme("cactus-light");
      diffEditorRef.current = editor;
      applyHighlights();
    },
    [applyHighlights]
  );

  return editorType === "monaco" ? (
    <div style={{ height, border: "1px solid #e0e0e0" }}>
      {HIGHLIGHT_STYLES}
      <MonacoDiffEditor
        original={original}
        modified={modified}
        language={language}
        theme={"cactus-light"}
        onMount={handleEditorMount}
        beforeMount={defineThemes}
        keepCurrentOriginalModel={true}
        keepCurrentModifiedModel={true}
        options={{
          readOnly: true,
          renderSideBySide: true,
          renderGutterMenu: false,
          minimap: { enabled: true },
          scrollBeyondLastLine: false,
          fontSize: 13,
          fontFamily:
            "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
        }}
      />
    </div>
  ) : (
    <ReactDiffViewer
      oldValue={original}
      newValue={modified}
      splitView={true}
      useDarkTheme={false}
    />
  );
};

const CodeEditor = ({
  value,
  language = "json",
  onChange,
  readOnly = false,
  height = "70vh",
  padding = 15,
}) => {
  const [editorType] = useEditorPreference();

  const handleEditorMount = useCallback((_editor, monaco) => {
    defineThemes(monaco);
    monaco.editor.setTheme("cactus-light");
  }, []);

  return editorType === "monaco" ? (
    <div style={{ height, border: "1px solid #e0e0e0" }}>
      <MonacoEditor
        value={value}
        language={language}
        theme={"cactus-light"}
        onChange={readOnly ? undefined : onChange}
        onMount={handleEditorMount}
        beforeMount={defineThemes}
        options={{
          readOnly,
          minimap: { enabled: true },
          scrollBeyondLastLine: false,
          wordWrap: "on",
          fontSize: 13,
          fontFamily:
            "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
        }}
      />
    </div>
  ) : (
    <ReactCodeEditor
      value={value}
      language={language}
      onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
      padding={padding}
      readOnly={readOnly}
      style={{
        backgroundColor: "#f5f5f5",
        fontFamily:
          "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
        minHeight: readOnly ? undefined : "400px",
        maxHeight: height,
        overflow: "auto",
      }}
    />
  );
};

export default CodeEditor;
